import React from 'react';
import { Lock } from 'lucide-react';
import IconTile from './IconTile';

const TONE_TO_GRADIENT = {
  blue: 'lockBlue',
  orange: 'lockOrange',
  green: 'lockGreen',
  purple: 'lockPurple',
  indigo: 'lockIndigo',
};

const TONE_TO_ICON = {
  blue: 'text-apple-blue',
  orange: 'text-apple-orange',
  green: 'text-apple-green',
  purple: 'text-apple-purple',
  indigo: 'text-apple-indigo',
};

/**
 * Absolute paywall lock overlay. Parent must be `relative`.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.body='Included with a subscription or paid credits.']
 * @param {'blue'|'orange'|'green'|'purple'|'indigo'} [props.tone='blue']
 * @param {boolean} [props.rounded] — add rounded-2xl on overlay
 * @param {boolean} [props.compact] — smaller tile for score cells
 * @param {React.ReactNode} [props.action] — optional CTA slot
 * @param {string} [props.className]
 */
export default function LockOverlay({
  title,
  body = 'Included with a subscription or paid credits.',
  tone = 'blue',
  rounded = false,
  compact = false,
  action,
  className = '',
}) {
  const gradient = TONE_TO_GRADIENT[tone] || TONE_TO_GRADIENT.blue;
  const iconColor = TONE_TO_ICON[tone] || TONE_TO_ICON.blue;

  return (
    <div
      className={`absolute inset-0 bg-critic-bg-deep/88 backdrop-blur-sm z-10 flex items-center justify-center ${
        rounded ? 'rounded-2xl' : ''
      } ${className}`.trim()}
    >
      <div className={`text-center ${compact ? 'px-4' : 'p-6'}`}>
        <IconTile size={compact ? 'lg' : 'xl'} gradient={gradient} className={`mx-auto ${compact ? 'mb-3' : 'mb-4'}`}>
          <Lock className={compact ? `h-6 w-6 ${iconColor}` : `h-8 w-8 ${iconColor}`} />
        </IconTile>
        {title && (
          <h3
            className={`font-display font-semibold text-critic-text-primary ${
              compact ? 'text-sm mb-1' : 'text-xl mb-2'
            }`}
          >
            {title}
          </h3>
        )}
        {body && (
          <p
            className={`text-critic-text-secondary font-normal max-w-xs mx-auto ${
              compact ? 'text-xs' : 'text-sm'
            }`}
          >
            {body}
          </p>
        )}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}
